import { REQUEST_METHOD } from '@/constants';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { WORKSPACE_STORAGE_KEY } from '@/constants/workspace';
import type { IPersonaItem } from '@/types/personas';
import {
  ICreateWorkspaceRequest,
  ICreateWorkspaceResponse,
  ICreditAnalytics,
  ICreditUsageHistory,
  IJoinWorkspaceRequest,
  ISendInvitationRequest,
  ISwitchWorkspaceRequest,
  IUpdateMemberPermissionsRequest,
  IWorkspace,
  IWorkspaceCreditsV3,
  IWorkspaceInvitation,
  IWorkspaceListItem,
  IWorkspaceMember,
  IWorkspaceRole,
  IWorkspaceRolesResponse,
  IWorkspacesResponse,
  IWorkspaceWithMembership,
  WorkspaceUiPersona,
  TaskDraftLanguagePreference,
} from '@/types/workspace';
import { apiInstance } from '@/utils';

// ====================================================================
// Helper Functions
// ====================================================================

/**
 * Get workspace ID from parameter or localStorage
 * Throws error if workspace ID cannot be determined
 */
const getWorkspaceId = (workspaceId?: number): number => {
  let targetWorkspaceId = workspaceId;
  
  if (!targetWorkspaceId && typeof window !== 'undefined') {
    const storedWorkspaceId = localStorage.getItem(WORKSPACE_STORAGE_KEY);
    if (storedWorkspaceId && storedWorkspaceId !== 'null' && storedWorkspaceId !== 'undefined') {
      targetWorkspaceId = parseInt(storedWorkspaceId, 10);
    }
  }

  if (!targetWorkspaceId || isNaN(targetWorkspaceId)) {
    throw new Error('Workspace ID is required for this operation');
  }

  return targetWorkspaceId;
};

export interface CompanyResearchContext {
  name?: string;
  domain?: string;
  company_description?: string;
  value_prop_one_liner?: string;
  icp_primary?: string;
  icp_secondary?: string;
  industry?: string;
  primary_use_cases?: string[] | string;
  differentiators?: string[] | string;
  common_objections?: Array<{
    theme: string;
    objection: string;
    short_response: string;
  }>;
  // Legacy fields for backwards compatibility
  snippet?: string;
  title?: string;
  research?: {
    markdown?: string;
    objections_md?: string;
  };
  use_cases?: string[] | string;
  specialities?: string[] | string;
  [key: string]: unknown;
}

export interface ColdCallContext {
  call_script?: string;
  discovery_questions?: string;
  objections?: Array<{
    objection: string;
    response: string;
  }>;
  call_closing?: string;
  value_proposition?: string;
  social_proof?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export type CreditUsageMetadata = Record<string, unknown>;

// ====================================================================
// Core Workspace Operations
// ====================================================================

export const getWorkspaces = async (): Promise<IWorkspacesResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.listWorkspaces,
  });

  return result.data;
};

export const getActiveWorkspace = async (): Promise<{
  activeWorkspace: IWorkspaceWithMembership;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getActiveWorkspace,
  });

  return result.data;
};

export const getWorkspaceById = async (
  workspaceId: number,
): Promise<{ workspace: IWorkspaceWithMembership }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getWorkspace(workspaceId),
  });

  return result.data;
};

export const createWorkspace = async (
  data: ICreateWorkspaceRequest,
): Promise<ICreateWorkspaceResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.createWorkspace,
  });

  return result.data;
};

export const updateWorkspace = async (
  workspaceId: number,
  data: Partial<IWorkspace>,
): Promise<{ message: string; workspace: IWorkspace }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    data,
    url: API_ENDPOINTS.updateWorkspace(workspaceId),
  });

  return result.data;
};

export const deleteWorkspace = async (workspaceId: number): Promise<{ message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.deleteWorkspace(workspaceId),
  });

  return result.data;
};

// ====================================================================
// Workspace Switching & Joining
// ====================================================================

export const switchWorkspace = async (
  data: ISwitchWorkspaceRequest,
): Promise<{ message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.switchWorkspace,
  });

  return result.data;
};

export const joinWorkspace = async (
  data: IJoinWorkspaceRequest,
): Promise<{
  message: string;
  workspace?: IWorkspaceWithMembership;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.joinWorkspace,
  });

  return result.data;
};

// ====================================================================
// Member Management
// ====================================================================

export const getWorkspaceMembers = async (
  workspaceId: number,
): Promise<{ members: IWorkspaceMember[]; seatLimit?: number | null; currentSeats?: number }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getWorkspaceMembers(workspaceId),
  });

  return result.data;
};

export const getWorkspaceRoles = async (
  workspaceId: number,
): Promise<IWorkspaceRolesResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getWorkspaceRoles(workspaceId),
  });

  return result.data;
};

export const createWorkspaceRole = async (
  workspaceId: number,
  data: { name: string; permissions: string[] },
): Promise<{ role: IWorkspaceRole }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.createWorkspaceRole(workspaceId),
    data,
  });
  return result.data;
};

export const updateWorkspaceRole = async (
  workspaceId: number,
  roleId: string,
  data: { name?: string; permissions?: string[] },
): Promise<{ role: IWorkspaceRole }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    url: API_ENDPOINTS.updateWorkspaceRole(workspaceId, roleId),
    data,
  });
  return result.data;
};

export const deleteWorkspaceRole = async (
  workspaceId: number,
  roleId: string,
): Promise<{ deleted: boolean; reassignedCount: number }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.deleteWorkspaceRole(workspaceId, roleId),
  });
  return result.data;
};

export const assignMemberRole = async (
  workspaceId: number,
  memberId: string,
  roleId: string,
): Promise<{ member: { role_id: string; role: string; is_admin: boolean } }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    url: API_ENDPOINTS.assignMemberRole(workspaceId, memberId),
    data: { roleId },
  });
  return result.data;
};

export const updateMemberUiPersona = async (
  workspaceId: number,
  memberId: string,
  uiPersona: WorkspaceUiPersona,
): Promise<{ message: string; member: { ui_persona: WorkspaceUiPersona } }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    url: API_ENDPOINTS.updateMemberUiPersona(workspaceId, memberId),
    data: { uiPersona },
  });
  return result.data;
};

export const updateMyTaskDraftLanguage = async (
  workspaceId: number,
  taskDraftLanguagePreference: TaskDraftLanguagePreference,
): Promise<{ message: string; member: { task_draft_language_preference: TaskDraftLanguagePreference } }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    url: API_ENDPOINTS.updateMyTaskDraftLanguage(workspaceId),
    data: { taskDraftLanguagePreference },
  });
  return result.data;
};

export const updateMemberTaskDraftLanguage = async (
  workspaceId: number,
  memberId: string,
  taskDraftLanguagePreference: TaskDraftLanguagePreference,
): Promise<{ message: string; member: { task_draft_language_preference: TaskDraftLanguagePreference } }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    url: API_ENDPOINTS.updateMemberTaskDraftLanguage(workspaceId, memberId),
    data: { taskDraftLanguagePreference },
  });
  return result.data;
};

export const updateMemberPermissions = async (
  workspaceId: number,
  memberId: string,
  data: IUpdateMemberPermissionsRequest,
): Promise<{ message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    data,
    url: API_ENDPOINTS.updateMemberPermissions(workspaceId, memberId),
  });

  return result.data;
};

export const removeMember = async (
  workspaceId: number,
  memberId: string,
): Promise<{ message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.removeMember(workspaceId, memberId),
  });

  return result.data;
};

// requestCreditAccess removed (#2711): credit eligibility is now role-based.

// ====================================================================
// Admin Management (Multi-Admin Feature)
// ====================================================================

export const promoteToAdmin = async (
  workspaceId: number,
  userId: string,
): Promise<{ message: string; user: IWorkspaceMember }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.promoteToAdmin(workspaceId, userId),
  });

  return result.data;
};

export const demoteFromAdmin = async (
  workspaceId: number,
  userId: string,
): Promise<{ message: string; user: IWorkspaceMember }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.demoteFromAdmin(workspaceId, userId),
  });

  return result.data;
};

// ====================================================================
// MFA Management (Admin-Only)
// ====================================================================

export const disableMemberMfa = async (
  workspaceId: number,
  memberId: string,
): Promise<{ message: string; targetUserId: string; targetUserEmail: string; disabledBy: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.disableMemberMfa(workspaceId, memberId),
  });

  return result.data;
};

// ====================================================================
// Company Research
// ====================================================================

export const getCompanyResearch = async (
  workspaceId: number,
): Promise<{
  workspaceId?: number;
  workspaceName?: string;
  companyWebsite?: string | null;
  researchContext?: CompanyResearchContext;
  hasResearch?: boolean;
  lastUpdated?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getCompanyResearch(workspaceId),
  });
  return result.data;
};

export const updateCompanyResearch = async (
  workspaceId: number,
  researchData: CompanyResearchContext,
): Promise<{
  workspaceId?: number;
  workspaceName?: string;
  companyWebsite?: string | null;
  researchContext?: CompanyResearchContext;
  lastUpdated?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    url: API_ENDPOINTS.getCompanyResearch(workspaceId),
    data: researchData,
  });
  return result.data;
};

export const generateCompanyResearch = async (
  workspaceId: number,
  forceRefresh = true,
): Promise<{
  workspaceId?: number;
  companyName?: string;
  researchContext?: CompanyResearchContext;
  message?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.triggerCompanyResearch(workspaceId),
    data: { forceRefresh },
  });
  return result.data;
};

// ====================================================================
// Job Posting Context
// ====================================================================

export const generateJobPostingContext = async (
  workspaceId: number,
): Promise<{
  message?: string;
  signalsProcessed?: number;
  successCount?: number;
  errorCount?: number;
  errors?: Array<{ signalId: string; companyName: string; error: string }>;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.triggerJobPostingContextGeneration(workspaceId),
  });
  return result.data;
};

export const generateJobTitles = async (
  workspaceId: number,
): Promise<{
  jobTitles: string[];
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.generateJobTitles(workspaceId),
  });
  return result.data;
};

export const generateTechnologies = async (
  workspaceId: number,
): Promise<{
  technologies: string[];
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.generateTechnologies(workspaceId),
  });
  return result.data;
};

export type GeneratedSignalConfig = {
  config_name: string;
  title: string[];
  technologies: string[];
  job_functions: string[];
  industries: string[];
  seniority_level: string[];
  employment_type: string[];
};

export const generateSignalConfigs = async (
  workspaceId: number,
): Promise<{
  configs: GeneratedSignalConfig[];
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.generateSignalConfigs(workspaceId),
  });
  return result.data;
};

export const generateMarketIntelConfig = async (
  workspaceId: number,
  existingConfig?: { focus_description?: string; focus_tags?: string[] },
): Promise<{
  focus_description: string;
  focus_tags: string[];
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.generateMarketIntelConfig(workspaceId),
    data: existingConfig,
  });
  return result.data;
};

export const generateCommunityMentionsConfig = async (
  workspaceId: number,
  existingConfig?: { keywords?: string[]; subreddits?: string[] },
): Promise<{
  keywords: string[];
  subreddits: string[];
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.generateCommunityMentionsConfig(workspaceId),
    data: existingConfig,
  });
  return result.data;
};

// ====================================================================
// Cold Call Context
// ====================================================================

export const getColdCallContext = async (
  workspaceId: number,
): Promise<{
  workspaceId?: number;
  workspaceName?: string;
  coldCallContext?: ColdCallContext;
  hasContext?: boolean;
  lastUpdated?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getColdCallContext(workspaceId),
  });
  return result.data;
};

export const updateColdCallContext = async (
  workspaceId: number,
  coldCallData: ColdCallContext,
): Promise<{
  workspaceId?: number;
  workspaceName?: string;
  coldCallContext?: ColdCallContext;
  lastUpdated?: string;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PUT,
    url: API_ENDPOINTS.updateColdCallContext(workspaceId),
    data: coldCallData,
  });
  return result.data;
};

export const triggerColdCallGeneration = async (
  workspaceId: number,
  forceRefresh: boolean = false,
): Promise<{
  message: string;
  workspaceId: number;
  companyName?: string;
  coldCallContext?: ColdCallContext;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.getColdCallContext(workspaceId),
    data: { forceRefresh },
  });
  return result.data;
};

export const deleteColdCallContext = async (
  workspaceId: number,
): Promise<{ message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.deleteColdCallContext(workspaceId),
  });
  return result.data;
};

// ====================================================================
// Join Code Management
// ====================================================================

export const generateJoinCode = async (
  workspaceId: number,
): Promise<{ message: string; join_code: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.generateJoinCode(workspaceId),
  });

  return result.data;
};

// ====================================================================
// Credit Management
// ====================================================================

export const getWorkspaceCredits = async (
  workspaceId?: number,
): Promise<{ credits: IWorkspaceCreditsV3 | null }> => {
  const targetWorkspaceId = getWorkspaceId(workspaceId);

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getWorkspaceCredits(targetWorkspaceId),
  });

  return result.data;
};

export const getCreditHistory = async (
  params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    creditType?: 'free' | 'paid' | 'ai' | 'video_avatar';
    feature?: string;
  },
  workspaceId?: number,
): Promise<{
  history: ICreditUsageHistory[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}> => {
  const targetWorkspaceId = getWorkspaceId(workspaceId);

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    params,
    url: API_ENDPOINTS.getCreditHistory(targetWorkspaceId),
  });

  return result.data;
};

export interface IEmailUnsubscribe {
  id: number;
  email: string;
  audience_id: number | null;
  play_id: number | null;
  play_name: string | null;
  unsubscribed_at: string;
  ip: string | null;
}

export const getEmailUnsubscribes = async (
  params: { page?: number; limit?: number; email?: string },
  workspaceId?: number,
): Promise<{
  unsubscribes: IEmailUnsubscribe[];
  pagination: { total: number; page: number; limit: number };
}> => {
  const targetWorkspaceId = getWorkspaceId(workspaceId);
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    params,
    url: API_ENDPOINTS.getEmailUnsubscribes(targetWorkspaceId),
  });
  return result.data;
};

export const deleteEmailUnsubscribe = async (
  id: number,
  workspaceId?: number,
): Promise<{ success: boolean; removed: { id: number; email: string } }> => {
  const targetWorkspaceId = getWorkspaceId(workspaceId);
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.deleteEmailUnsubscribe(targetWorkspaceId, id),
  });
  return result.data;
};

export const getCreditAnalytics = async (
  params?: {
    startDate?: string;
    endDate?: string;
  },
  workspaceId?: number,
): Promise<{ analytics: ICreditAnalytics }> => {
  const targetWorkspaceId = getWorkspaceId(workspaceId);

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    params,
    url: API_ENDPOINTS.getCreditAnalytics(targetWorkspaceId),
  });

  return result.data;
};

// ====================================================================
// Invitation Management
// ====================================================================

export const sendInvitation = async (
  data: ISendInvitationRequest,
  workspaceId?: number,
): Promise<{ message: string; invitation: IWorkspaceInvitation }> => {
  const url = workspaceId
    ? API_ENDPOINTS.sendInvitationToWorkspace(workspaceId)
    : API_ENDPOINTS.sendInvitation;

  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url,
  });

  return result.data;
};

export const getWorkspaceInvitations = async (
  workspaceId?: number,
): Promise<{ invitations: IWorkspaceInvitation[] }> => {
  const url = workspaceId
    ? API_ENDPOINTS.getWorkspaceInvitations(workspaceId)
    : API_ENDPOINTS.getInvitations;

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url,
  });

  return result.data;
};

export const revokeInvitation = async (invitationId: number): Promise<{ message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.revokeInvitation(invitationId),
  });

  return result.data;
};

export const resendInvitation = async (invitationId: number): Promise<{ message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.resendInvitation(invitationId),
  });

  return result.data;
};

export const acceptInvitation = async (
  token: string,
): Promise<{
  message: string;
  workspace: IWorkspaceWithMembership;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.acceptInvitation(token),
  });

  return result.data;
};

// ====================================================================
// Pending Invitations (User-specific)
// ====================================================================

interface IPendingInvitation {
  id: number;
  vendorId: number;
  invitedEmail: string;
  invitationToken: string;
  expiresAt: string;
  createdAt: string;
  invitedFirstName: string | null;
  invitedLastName: string | null;
  invitedRole: string;
  message: string | null;
  workspaceName: string;
  inviterFirstName: string | null;
  inviterLastName: string | null;
  inviterEmail: string;
}

/**
 * Get pending invitations for the authenticated user
 * Used as fallback during login when Supabase metadata is missing
 */
export const getPendingInvitations = async (): Promise<{
  invitations: IPendingInvitation[];
  count: number;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getPendingInvitations,
  });

  return result.data;
};

/**
 * Auto-accept all pending workspace invitations for the authenticated user.
 * Called during password and MFA login flows so invited users are immediately
 * added to their workspace without a manual acceptance step.
 * Also sets provider_status='Approved' so OAuth login is not blocked in future.
 */
export const autoAcceptPendingInvitations = async (): Promise<{ count: number; accepted: { workspaceId: number; workspaceName: string }[] }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.autoAcceptInvitations,
  });

  return result.data;
};

export const generatePersonas = async (
  workspaceId: number,
): Promise<{
  personas: IPersonaItem[];
  count: number;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.generatePersonas(workspaceId),
  });
  return result.data;
};
